/**
 * Environment Configuration System
 *
 * Centralized, type-safe environment management for Learnbahn frontend.
 *
 * Architecture:
 * - Environment enum: Type-safe environment values (imported from shared config)
 * - AppConfig interface: All runtime configuration in one place
 * - loadConfig(): Singleton loader with validation and defaults
 * - Helper functions: isProduction(), isDevelopment()
 */

import { EnvVarKey } from './keys';
import {
  APP_TIMEZONE,
  Environment,
  parseEnvironment,
} from '../../../../config/environments';

// Re-export for backward compatibility
export { APP_TIMEZONE, Environment };

/**
 * Application Configuration Interface
 * Single source of truth for all runtime configuration
 */
export interface AppConfig {
  // Environment
  environment: Environment;

  // Application
  appName: string;
  appVersion: string;

  // Service URLs
  serviceAPIUrl: string;
  serviceFrontendUrl: string;
  serviceWebUrl: string;
  apiTimeout: number;

  // Authentication
  authDomain: string;
  authClientId: string;

  // Analytics
  gaId: string;
  hotjarId: string;

  // Feature Flags
  enableDebug: boolean;
}

/**
 * Helper to get environment variable with type safety
 * Use EnvVarKey enum to prevent typos
 *
 * IMPORTANT: Must use direct property access, not bracket notation!
 * Vite requires static analysis of import.meta.env for build-time replacement
 */
function getEnvVar(key: EnvVarKey): string | undefined {
  // Use switch with direct property access so Vite can statically analyze
  switch (key) {
    case EnvVarKey.VITE_ENVIRONMENT:
      return import.meta.env.VITE_ENVIRONMENT;
    case EnvVarKey.NODE_ENV:
      return import.meta.env.NODE_ENV;
    case EnvVarKey.VITE_APP_ENV:
      return import.meta.env.VITE_APP_ENV;
    case EnvVarKey.MODE:
      return import.meta.env.MODE;
    case EnvVarKey.DEV:
      return import.meta.env.DEV ? 'true' : 'false';
    case EnvVarKey.VITE_SERVICE_API_URL:
      return import.meta.env.VITE_SERVICE_API_URL;
    case EnvVarKey.VITE_SERVICE_FRONTEND_URL:
      return import.meta.env.VITE_SERVICE_FRONTEND_URL;
    case EnvVarKey.VITE_SERVICE_WEB_URL:
      return import.meta.env.VITE_SERVICE_WEB_URL;
    case EnvVarKey.VITE_API_TIMEOUT:
      return import.meta.env.VITE_API_TIMEOUT;
    case EnvVarKey.VITE_AUTH_DOMAIN:
      return import.meta.env.VITE_AUTH_DOMAIN;
    case EnvVarKey.VITE_AUTH_CLIENT_ID:
      return import.meta.env.VITE_AUTH_CLIENT_ID;
    case EnvVarKey.VITE_GA_ID:
      return import.meta.env.VITE_GA_ID;
    case EnvVarKey.VITE_HOTJAR_ID:
      return import.meta.env.VITE_HOTJAR_ID;
    case EnvVarKey.VITE_DEBUG:
      return import.meta.env.VITE_DEBUG;
    case EnvVarKey.VITE_APP_VERSION:
      return import.meta.env.VITE_APP_VERSION;
    default:
      return undefined;
  }
}

/**
 * Type-safe config value getter
 * Use this to access config values without direct property access
 */
export function getConfigValue<K extends keyof AppConfig>(
  key: K
): AppConfig[K] {
  return getConfig()[key];
}

/**
 * Detect current environment from environment variables
 * Uses shared parseEnvironment function for consistency
 *
 * Priority:
 * 1. ENVIRONMENT (explicit override)
 * 2. NODE_ENV (framework default)
 * 3. Default to 'development'
 */
function detectEnvironment(): Environment {
  // Try ENVIRONMENT first (explicit override)
  const envVar = getEnvVar(EnvVarKey.VITE_ENVIRONMENT);
  if (envVar) {
    return parseEnvironment(envVar);
  }

  // Fallback to NODE_ENV
  const nodeEnv = getEnvVar(EnvVarKey.NODE_ENV);
  if (nodeEnv) {
    return parseEnvironment(nodeEnv);
  }

  // Default: development
  return Environment.DEVELOPMENT;
}

/**
 * Parse integer from environment variable with fallback
 */
function parseEnvInt(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Parse boolean from environment variable
 */
function parseEnvBool(value: string | undefined, fallback: boolean): boolean {
  if (!value) return fallback;
  return value.toLowerCase() === 'true';
}

/**
 * Get app version from package.json or environment
 */
function getAppVersion(): string {
  return getEnvVar(EnvVarKey.VITE_APP_VERSION) || '0.1.0';
}

/**
 * Load and validate application configuration
 * Singleton pattern - config is loaded once and cached
 */
let cachedConfig: AppConfig | null = null;

export function loadConfig(): AppConfig {
  // Return cached config if already loaded
  if (cachedConfig) {
    return cachedConfig;
  }

  const env = detectEnvironment();

  // Build configuration
  const config: AppConfig = {
    // Environment
    environment: env,

    // Application
    appName: 'learnbahn-frontend',
    appVersion: getAppVersion(),

    // Service URLs
    serviceAPIUrl:
      getEnvVar(EnvVarKey.VITE_SERVICE_API_URL) || 'https://api.learnbahn.com',
    serviceFrontendUrl:
      getEnvVar(EnvVarKey.VITE_SERVICE_FRONTEND_URL) ||
      'https://mono-learnbahn-frontend.pages.dev',
    serviceWebUrl:
      getEnvVar(EnvVarKey.VITE_SERVICE_WEB_URL) ||
      'https://mono-learnbahn-web.pages.dev',
    apiTimeout: parseEnvInt(getEnvVar(EnvVarKey.VITE_API_TIMEOUT), 60000),

    // Authentication
    authDomain: getEnvVar(EnvVarKey.VITE_AUTH_DOMAIN) || '',
    authClientId: getEnvVar(EnvVarKey.VITE_AUTH_CLIENT_ID) || '',

    // Analytics
    gaId: getEnvVar(EnvVarKey.VITE_GA_ID) || '',
    hotjarId: getEnvVar(EnvVarKey.VITE_HOTJAR_ID) || '',

    // Feature Flags
    enableDebug: parseEnvBool(
      getEnvVar(EnvVarKey.VITE_DEBUG),
      env === Environment.DEVELOPMENT
    ),
  };

  // Cache and return
  cachedConfig = config;
  return config;
}

/**
 * Get current configuration (singleton)
 * Use this instead of loadConfig() for better semantics
 */
export function getConfig(): AppConfig {
  return loadConfig();
}

/**
 * Helper: Check if running in production
 */
export function isProduction(): boolean {
  return getConfig().environment === Environment.PRODUCTION;
}

/**
 * Helper: Check if running in development
 */
export function isDevelopment(): boolean {
  return getConfig().environment === Environment.DEVELOPMENT;
}
